import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BadgeEuro, Building2, ChevronLeft, ChevronRight, FolderOpen, Users } from "lucide-react";
import { KpiCard } from "@/components/KpiCard";
import { Badge } from "@/components/Badge";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { STAGE_COLORS } from "@/lib/constants";
import { formatMonth } from "@/lib/dates";
import { getDashboardRete, isAccessoSospeso } from "@/api/rete";
import type { DashboardReteResponse } from "@/types/rete";
import { AccessoSospeso } from "./AccessoSospeso";

function euro(n: number): string {
  return `€${n.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
}

/** Mese dall'URL (`?anno=&mese=`), altrimenti quello corrente. */
function meseDaUrl(sp: URLSearchParams): { anno: number; mese: number } {
  const oggi = new Date();
  const anno = Number(sp.get("anno")) || oggi.getFullYear();
  const mese = Number(sp.get("mese")) || oggi.getMonth() + 1;
  return { anno, mese: Math.min(12, Math.max(1, mese)) };
}

function spostaMese(anno: number, mese: number, delta: number): { anno: number; mese: number } {
  const d = new Date(anno, mese - 1 + delta, 1);
  return { anno: d.getFullYear(), mese: d.getMonth() + 1 };
}

const STATO_VUOTO = (
  <EmptyState
    icon={<Building2 size={26} className="text-brand" />}
    title="Nessuna agenzia in carico"
    description="Le agenzie ti vengono assegnate dall'amministrazione oppure entrano dalle tue affiliazioni."
  />
);

export function DashboardRete() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { anno, mese } = meseDaUrl(sp);
  const [data, setData] = useState<DashboardReteResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sospeso, setSospeso] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getDashboardRete(anno, mese)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (cancelled) return;
        if (isAccessoSospeso(e)) setSospeso(true);
        else setError("Impossibile caricare il cruscotto.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [anno, mese]);

  function vaiAlMese(delta: number) {
    const m = spostaMese(anno, mese, delta);
    setSp({ anno: String(m.anno), mese: String(m.mese) });
  }

  if (sospeso) return <AccessoSospeso />;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted rounded-lg animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-card rounded-xl border border-border p-5 space-y-3">
              <div className="h-3 w-24 bg-muted rounded animate-pulse" />
              <div className="h-8 w-16 bg-muted rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="bg-card rounded-xl border border-border p-5 space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-12 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }
  if (error || !data) return <ErrorState message={error ?? "Errore"} />;

  // Un'agenzia può non essere più in carico oggi e comunque avere pratiche maturate nel
  // mese scelto (spec: incarico chiuso di recente): lo stato vuoto scatta solo quando non
  // c'è proprio nulla da mostrare, non sul solo conteggio delle agenzie di oggi.
  const vuoto = data.agenzie === 0 && data.righe.length === 0;

  const meseMinuscolo = formatMonth(data.month);
  const meseLeggibile = meseMinuscolo.charAt(0).toUpperCase() + meseMinuscolo.slice(1);
  const perStato = Object.entries(data.per_stato);
  const maxStato = Math.max(...perStato.map(([, c]) => c), 1);
  const bottoneMese =
    "min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg border border-border bg-card text-muted-foreground hover:text-text transition-colors";

  function apriPratiche(agenziaId: string) {
    navigate(`/rete/pratiche?agenzia=${encodeURIComponent(agenziaId)}`);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-text">La tua rete</h2>
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Mese prima" onClick={() => vaiAlMese(-1)} className={bottoneMese}>
            <ChevronLeft size={18} />
          </button>
          <span className="min-w-[9rem] text-center text-sm font-medium text-text">{meseLeggibile}</span>
          <button type="button" aria-label="Mese dopo" onClick={() => vaiAlMese(1)} className={bottoneMese}>
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {vuoto ? (
        STATO_VUOTO
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="Agenzie" value={data.agenzie} icon={<Building2 size={16} />} variant="hero" />
            <KpiCard
              label="Pratiche del mese"
              value={data.pratiche_mese}
              subtitle={`mese scorso: ${data.pratiche_mese_precedente}`}
              icon={<FolderOpen size={16} />}
            />
            <KpiCard
              label="Provvigioni del mese"
              value={euro(data.provvigioni_mese)}
              icon={<BadgeEuro size={16} />}
              accentClass="border-l-amber-400"
            />
            <KpiCard label="Clienti" value={data.clienti} icon={<Users size={16} />} accentClass="border-l-blue-400" />
          </div>

          {perStato.length > 0 && (
            <div className="bg-card rounded-xl border border-border p-5">
              <h3 className="font-semibold text-text mb-4">Per stato</h3>
              <div className="space-y-2.5">
                {perStato.map(([stato, count]) => {
                  const color = STAGE_COLORS[stato] ?? STAGE_COLORS["Bozza"];
                  return (
                    <div key={stato} className="flex items-center gap-3">
                      <div className="w-32 flex-shrink-0">
                        <Badge bg={color.bg} text={color.text}>{stato}</Badge>
                      </div>
                      <div className="flex-1 h-2.5 rounded-full bg-surface overflow-hidden">
                        <div className="h-full rounded-full bg-brand" style={{ width: `${(count / maxStato) * 100}%` }} />
                      </div>
                      <span className="w-8 text-right text-sm font-bold text-text tabular-nums">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <h3 className="font-semibold text-text px-5 pt-5 pb-3">Per agenzia</h3>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agenzia</TableHead>
                    <TableHead className="text-right">Pratiche</TableHead>
                    <TableHead className="text-right">Provvigioni</TableHead>
                    <TableHead className="text-right hidden md:table-cell">Clienti</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.righe.map((r) => (
                    <TableRow
                      key={r.agenzia_id}
                      role="button"
                      tabIndex={0}
                      onClick={() => apriPratiche(r.agenzia_id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          apriPratiche(r.agenzia_id);
                        }
                      }}
                      className="cursor-pointer [&_td]:py-3"
                    >
                      <TableCell className="font-medium text-text">{r.agenzia_nome}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.pratiche_mese}</TableCell>
                      <TableCell className="text-right tabular-nums">{euro(r.provvigioni_mese)}</TableCell>
                      <TableCell className="text-right tabular-nums hidden md:table-cell">{r.clienti}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
